<template>
  <div :class="wrapperClasses">
    <FormLabel
      v-if="$slots.label"
      :tooltip="tooltip"
      :is-required="isRequired"
      :id="id"
      class="tw-mb-2"
    >
      <slot name="label" />
    </FormLabel>
    <div
      ref="selectWrapperRef"
      class="tw-form-multiselect tw-relative tw-rounded-md tw-border-0 tw-p-0 tw-shadow-sm"
    >
      <Multiselect
        v-model="value"
        v-bind="$attrs"
        :options="asyncOptions"
        label="label"
        track-by="label"
        preserve-search
        :tabindex="$attrs.disabled ? -1 : 0"
        :test_id="$attrs.test_id"
        :allow-empty="allowEmpty"
        :internal-search="false"
        :loading="isLoading"
        @search-change="asyncSearch"
        :placeholder="`Type to search... (min ${autocompleteMinChars} chars)`"
      />
    </div>
    <div v-if="hasErrors || $slots.caption" class="tw-mt-2">
      <FormErrors v-if="hasErrors" :id="id" :errors="errors" />
      <FormCaption v-if="$slots.caption" :class="{ 'tw-mt-1': hasErrors }">
        <slot name="caption" />
      </FormCaption>
    </div>
  </div>
</template>

<script setup lang="ts">
import Multiselect from 'vue-multiselect'
import FormLabel from './FormLabel.vue'
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import FormErrors from './FormErrors.vue'
import FormCaption from './FormCaption.vue'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import axios from 'axios'

const emit = defineEmits(['update:modelValue'])

const props = withDefaults(
  defineProps<{
    modelValue: string | number
    staticOptions: Array<{ label: string; value: string | number }>
    isError?: boolean
    errors?: string | string[]
    wrapperClasses?: string | string[] | Record<string, unknown>
    isRequired?: boolean
    tooltip?: string
    allowEmpty?: boolean
    autocompleteUrl: string
    autocompleteMinChars?: number
  }>(),
  {
    isError: false,
    errors: '',
    wrapperClasses: '',
    isRequired: false,
    tooltip: '',
    allowEmpty: true,
    autocompleteMinChars: 3,
  },
)

const id = `growthmentor-autocomplete-select-${getUniqueId()}`
const selectWrapperRef = ref(null)
const isLoading = ref(false)
const asyncOptions = ref([...props.staticOptions])

const hasErrors = computed(() => {
  if (!props.isError) return false
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})

const value = ref(
  asyncOptions.value.find((option) => option.value === props.modelValue),
)

watch(value, (newValue) => {
  emit('update:modelValue', newValue ? newValue.value : null)
})
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) return
    value.value = asyncOptions.value.find((option) => option.value === newValue)
  },
)
watch(
  () => props.isError,
  (newValue) => {
    if (newValue) {
      selectWrapperRef.value
        .querySelector('.multiselect__tags')
        .classList.add('multiselect__tags--error')
    } else {
      selectWrapperRef.value
        .querySelector('.multiselect__tags')
        .classList.remove('multiselect__tags--error')
    }
  },
)

const asyncSearchRequest = useDebounceFn(async (query: string) => {
  try {
    const { data } = await axios.get(props.autocompleteUrl, {
      params: { search: query },
    })
    asyncOptions.value = [...props.staticOptions, ...data.results]
  } catch (error) {
    console.error(error)
  } finally {
    isLoading.value = false
  }
}, 1000)

const asyncSearch = (query: string) => {
  if (query.length === 0) {
    asyncOptions.value = [...props.staticOptions]
    return
  }
  isLoading.value = true
  if (query.length < props.autocompleteMinChars) return

  asyncSearchRequest(query)
}
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>
